const LEAF_SAMPLE_MIN = 0
const LEAF_SAMPLE_MAX = 119
const AEROPONIC_HISTORY_BUCKETS = Object.freeze({ "24h": 300, "7d": 900, "30d": 3600 })
const AEROPONIC_HISTORY_QUALITY_FLAGS = Object.freeze(["valid", "estimated", "uncalibrated", "stale", "invalid", "unverified"])
const AEROPONIC_HISTORY_CALIBRATION_STATES = Object.freeze(["calibrated", "uncalibrated", "not_documented", "not_applicable"])
const AEROPONIC_HISTORY_TIME_QUALITIES = Object.freeze(["synchronized", "source_clock_unverified", "legacy_local_resolved", "server_received", "monotonic_reconstructed"])
const AEROPONIC_HISTORY_TIME_COUNTS = Object.freeze([...AEROPONIC_HISTORY_TIME_QUALITIES, "uncertain"])

const finite = (value) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))
const finiteOrNull = (value) => finite(value) ? Number(value) : null
const nonNegativeIntegerOrNull = (value) => Number.isInteger(value) && value >= 0 ? value : null
const safeText = (value) => typeof value === "string" ? value : ""
const requiredText = (value) => typeof value === "string" && value.trim().length > 0 ? value.trim() : null
const utcText = (value) => typeof value === "string"
     && /(?:Z|[+-]00:00)$/.test(value)
     && Number.isFinite(Date.parse(value)) ? value : null
const nullableNonNegativeFinite = (value) => value === null
     ? { valid: true, value: null }
     : finite(value) && Number(value) >= 0
     ? { valid: true, value: Number(value) }
     : { valid: false, value: null }

const knownUniqueList = (value, known) => {
     if (!Array.isArray(value)) return null
     const result = []
     for (const item of value) {
          if (typeof item !== "string" || !known.includes(item) || result.includes(item)) return null
          result.push(item)
     }
     return known.filter((item) => result.includes(item))
}

const countRecord = (value, names) => {
     if (!value || typeof value !== "object" || Array.isArray(value)) return null
     const result = {}
     for (const name of names) {
          const count = nonNegativeIntegerOrNull(value[name])
          if (count === null) return null
          result[name] = count
     }
     return result
}

const addCounts = (target, source, names) => names.forEach((name) => { target[name] += source[name] })
const emptyCounts = (names) => Object.fromEntries(names.map((name) => [name, 0]))
const countTotal = (counts, names) => names.reduce((total, name) => total + counts[name], 0)
const presentCountKeys = (counts, names) => names.filter((name) => counts[name] > 0)
const countStatus = (counts, names) => {
     const present = presentCountKeys(counts, names)
     return present.length === 0 ? "unknown" : present.length === 1 ? present[0] : "mixed"
}
const equalMembers = (left, right) => left.length === right.length && left.every((item) => right.includes(item))

const chartPoint = (sampleIndex, value, minimum, maximum) => {
     const span = Math.max(1, maximum - minimum)
     const x = 10 + ((sampleIndex - LEAF_SAMPLE_MIN) / (LEAF_SAMPLE_MAX - LEAF_SAMPLE_MIN)) * 580
     const y = 18 + ((maximum - value) / span) * 104
     return { sampleIndex, value, x, y }
}

/**
 * Builds a gap-preserving Leaf Sensor series from the protocol-v5 sample index.
 * Index 0..59 is outside-air purge and 60..119 is root-chamber measurement.
 */
export const buildLeafChart = (curve = []) => {
     const indexed = new Map()
     curve.forEach((sample) => {
          const sampleIndex = sample?.sample_index
          const value = sample?.plant_reference_mv
          if (!Number.isInteger(sampleIndex) || sampleIndex < LEAF_SAMPLE_MIN || sampleIndex > LEAF_SAMPLE_MAX) return
          if (sample?.plant_reference_valid !== true || sample?.plant_reference_health !== "ok"
               || typeof value !== "number" || !Number.isFinite(value)) return
          indexed.set(sampleIndex, value)
     })
     const entries = Array.from(indexed.entries()).sort((left, right) => left[0] - right[0])
     if (!entries.length) return { points: [], segments: [], minimum: null, maximum: null, phaseBoundaryX: 10 + (60 / 119) * 580 }

     const values = entries.map(([, value]) => value)
     const minimum = Math.min(...values)
     const maximum = Math.max(...values)
     const points = entries.map(([sampleIndex, value]) => chartPoint(sampleIndex, value, minimum, maximum))
     const segments = []
     points.forEach((point) => {
          const current = segments[segments.length - 1]
          const previous = current?.[current.length - 1]
          if (!current || point.sampleIndex !== previous.sampleIndex + 1) segments.push([point])
          else current.push(point)
     })
     return { points, segments, minimum, maximum, phaseBoundaryX: 10 + (60 / 119) * 580 }
}

export const aeroponicCycleAdvice = (cycle, language, t) => {
     if (!cycle) return t("aero.noAdvice")
     if (language === "de" && cycle.advice_de) return cycle.advice_de
     const known = new Set(["sensor_problem", "saturated", "learning", "drying_faster", "wetter_than_usual", "stable"])
     return t(`aero.verdict.${known.has(cycle.verdict) ? cycle.verdict : "unknown"}.text`)
}

export const aeroponicVerdictLabel = (verdict, t) => {
     const known = new Set(["sensor_problem", "saturated", "learning", "drying_faster", "wetter_than_usual", "stable"])
     return t(`aero.verdict.${known.has(verdict) ? verdict : "unknown"}.label`)
}

export const aeroponicMeasurementCoverage = (deployment) => {
     const nodes = Array.isArray(deployment?.nodes) ? deployment.nodes.filter((node) => !node.unlinked_at) : []
     const hasAeroponic = nodes.some((node) => node.node_type === "aeroponic_controller" && node.role === "primary_system")
     const hasEnvironment = nodes.some((node) => node.node_type === "chuck_environment_station" && node.role === "environment_station")
     const hasRun = Number.isInteger(Number(deployment?.run_id)) && Number(deployment.run_id) > 0
     return {
          hasAeroponic,
          hasEnvironment,
          hasRun,
          linkedDomains: [hasEnvironment && "environment", hasAeroponic && "irrigation", hasAeroponic && "response", hasRun && "outcomes"].filter(Boolean),
          structurallyComplete: hasAeroponic && hasEnvironment && hasRun,
     }
}

/**
 * Reduces the read-only Timescale response to the evidence the portal renders.
 * The mapper deliberately never forwards compressed blocks, envelopes or raw payloads.
 */
export const mapAeroponicTimescaleHistory = (document) => {
     const range = safeText(document?.range)
     const expectedBucketSeconds = AEROPONIC_HISTORY_BUCKETS[range]
     const bucketSeconds = finiteOrNull(document?.quality?.bucket_seconds)
     const rawEvidence = document?.evidence_layers?.raw
     const cycleCount = nonNegativeIntegerOrNull(rawEvidence?.cycle_count)
     const sampleCount = nonNegativeIntegerOrNull(rawEvidence?.sample_count)
     const eventCount = nonNegativeIntegerOrNull(rawEvidence?.event_count)
     const dataFrom = utcText(document?.data_from)
     const to = utcText(document?.to)
     const normalizedTruncated = document?.quality?.normalized_truncated
     const derivedTruncated = document?.quality?.derived_truncated
     const documentQualityFlags = knownUniqueList(document?.quality?.flags, AEROPONIC_HISTORY_QUALITY_FLAGS)
     if (document?.backend !== "timescaledb"
          || document?.storage_layout !== "aeroponic_cycle_blocks_v1"
          || document?.source_domain !== "aeroponic"
          || document?.read_only !== true
          || document?.controls_hardware !== false
          || expectedBucketSeconds === undefined
          || bucketSeconds !== expectedBucketSeconds
          || document?.quality?.aggregation_method !== "time_bucket_weighted_mean_min_max_count"
          || document?.quality?.interpolates !== false
          || document?.quality?.fills_missing_buckets !== false
          || rawEvidence?.payload_included !== false
          || typeof normalizedTruncated !== "boolean"
          || typeof derivedTruncated !== "boolean"
          || cycleCount === null
          || sampleCount === null
          || eventCount === null
          || !dataFrom
          || !to
          || Date.parse(dataFrom) > Date.parse(to)
          || documentQualityFlags === null
          || !Array.isArray(document?.series)) return null

     const series = []
     for (const [seriesIndex, entry] of document.series.entries()) {
          const code = requiredText(entry?.code)
          const componentKey = requiredText(entry?.component_key)
          const context = requiredText(entry?.context)
          const layer = requiredText(entry?.layer)
          const unit = requiredText(entry?.unit)
          const evidenceKind = requiredText(entry?.evidence_kind)
          const method = requiredText(entry?.method)
          const seriesVersion = requiredText(entry?.series_version)
          const seriesQualityFlags = knownUniqueList(entry?.quality_flags, AEROPONIC_HISTORY_QUALITY_FLAGS)
          const aggregation = entry?.aggregation
          const evidenceMatchesLayer = layer === "normalized"
               ? evidenceKind === "measured"
               : layer === "derived" && ["derived", "proxy"].includes(evidenceKind)
          if (!code || !componentKey || !context || !evidenceMatchesLayer
               || !unit
               || !method || !seriesVersion || seriesQualityFlags === null || typeof entry?.truncated !== "boolean"
               || aggregation?.method !== "time_bucket_weighted_mean_min_max_count"
               || aggregation?.bucket_seconds !== expectedBucketSeconds
               || aggregation?.time_basis !== "cycle_anchor_observed_at"
               || aggregation?.fills_missing_buckets !== false
               || aggregation?.interpolates !== false
               || !Array.isArray(entry?.points)) return null

          const points = []
          const seriesQualityCounts = emptyCounts(AEROPONIC_HISTORY_QUALITY_FLAGS)
          const seriesCalibrationCounts = emptyCounts(AEROPONIC_HISTORY_CALIBRATION_STATES)
          const seriesTimeQualityCounts = emptyCounts(AEROPONIC_HISTORY_TIME_COUNTS)
          let previousObservedAt = null
          let internalGapCount = 0
          for (const point of entry.points) {
               const observedAt = utcText(point?.observed_at)
               const firstReceivedAt = utcText(point?.first_received_at)
               const receivedAt = utcText(point?.received_at)
               const pointSampleCount = nonNegativeIntegerOrNull(point?.sample_count)
               const usableCount = nonNegativeIntegerOrNull(point?.usable_count)
               const value = finiteOrNull(point?.value)
               const minimum = finiteOrNull(point?.minimum)
               const maximum = finiteOrNull(point?.maximum)
               const qualityFlags = knownUniqueList(point?.quality_flags, AEROPONIC_HISTORY_QUALITY_FLAGS)
               const qualityCounts = countRecord(point?.quality_counts, AEROPONIC_HISTORY_QUALITY_FLAGS)
               const calibrationCounts = countRecord(point?.calibration_counts, AEROPONIC_HISTORY_CALIBRATION_STATES)
               const timeQualityCounts = countRecord(point?.time_quality_counts, AEROPONIC_HISTORY_TIME_COUNTS)
               const maximumClockUncertainty = nullableNonNegativeFinite(point?.maximum_clock_uncertainty_ms)
               const maximumValueUncertainty = nullableNonNegativeFinite(point?.maximum_value_uncertainty)
               if (!observedAt || !firstReceivedAt || !receivedAt
                    || Date.parse(firstReceivedAt) > Date.parse(receivedAt)
                    || pointSampleCount === null || pointSampleCount === 0 || usableCount === null || usableCount > pointSampleCount
                    || qualityFlags === null || qualityCounts === null || calibrationCounts === null || timeQualityCounts === null
                    || !maximumClockUncertainty.valid || !maximumValueUncertainty.valid
                    || countTotal(qualityCounts, AEROPONIC_HISTORY_QUALITY_FLAGS) !== pointSampleCount
                    || usableCount !== qualityCounts.valid + qualityCounts.estimated + qualityCounts.uncalibrated
                    || !equalMembers(qualityFlags, presentCountKeys(qualityCounts, AEROPONIC_HISTORY_QUALITY_FLAGS))
                    || ![0, pointSampleCount].includes(countTotal(calibrationCounts, AEROPONIC_HISTORY_CALIBRATION_STATES))
                    || countTotal(timeQualityCounts, AEROPONIC_HISTORY_TIME_QUALITIES) !== pointSampleCount
                    || timeQualityCounts.uncertain !== timeQualityCounts.source_clock_unverified
                         + timeQualityCounts.legacy_local_resolved
                         + timeQualityCounts.monotonic_reconstructed
                    || (usableCount === 0 && (value !== null || minimum !== null || maximum !== null))
                    || (usableCount > 0 && (value === null || minimum === null || maximum === null))
                    || (minimum !== null && maximum !== null && minimum > maximum)
                    || (value !== null && minimum !== null && (value < minimum || value > maximum))) return null
               const observedAtMilliseconds = Date.parse(observedAt)
               if (previousObservedAt !== null) {
                    const elapsed = observedAtMilliseconds - previousObservedAt
                    if (elapsed <= 0) return null
                    internalGapCount += Math.max(0, Math.floor(elapsed / (expectedBucketSeconds * 1000)) - 1)
               }
               previousObservedAt = observedAtMilliseconds
               addCounts(seriesQualityCounts, qualityCounts, AEROPONIC_HISTORY_QUALITY_FLAGS)
               addCounts(seriesCalibrationCounts, calibrationCounts, AEROPONIC_HISTORY_CALIBRATION_STATES)
               addCounts(seriesTimeQualityCounts, timeQualityCounts, AEROPONIC_HISTORY_TIME_COUNTS)
               points.push({
                    observedAt,
                    firstReceivedAt,
                    receivedAt,
                    value,
                    minimum,
                    maximum,
                    sampleCount: pointSampleCount,
                    usableCount,
                    qualityFlags,
                    qualityCounts,
                    qualityStatus: countStatus(qualityCounts, AEROPONIC_HISTORY_QUALITY_FLAGS),
                    calibrationCounts,
                    calibrationStatus: countStatus(calibrationCounts, AEROPONIC_HISTORY_CALIBRATION_STATES),
                    timeQualityCounts,
                    timeQualityStatus: countStatus(timeQualityCounts, AEROPONIC_HISTORY_TIME_QUALITIES),
                    maximumClockUncertaintyMs: maximumClockUncertainty.value,
                    maximumValueUncertainty: maximumValueUncertainty.value,
               })
          }
          if (!equalMembers(seriesQualityFlags, presentCountKeys(seriesQualityCounts, AEROPONIC_HISTORY_QUALITY_FLAGS))) return null
          series.push({
               id: `${layer}:${componentKey}:${code}:${seriesIndex}`,
               code,
               componentKey,
               context,
               layer,
               unit,
               evidenceKind,
               method,
               seriesVersion,
               qualityFlags: seriesQualityFlags,
               qualityCounts: seriesQualityCounts,
               qualityStatus: countStatus(seriesQualityCounts, AEROPONIC_HISTORY_QUALITY_FLAGS),
               calibrationCounts: seriesCalibrationCounts,
               calibrationStatus: countStatus(seriesCalibrationCounts, AEROPONIC_HISTORY_CALIBRATION_STATES),
               timeQualityCounts: seriesTimeQualityCounts,
               timeQualityStatus: countStatus(seriesTimeQualityCounts, AEROPONIC_HISTORY_TIME_QUALITIES),
               timeBasis: aggregation.time_basis,
               truncated: entry.truncated,
               pointCount: points.length,
               internalGapCount,
               firstObservedAt: points[0]?.observedAt || "",
               lastObservedAt: points[points.length - 1]?.observedAt || "",
               points,
          })
     }

     const normalizedSeriesCount = nonNegativeIntegerOrNull(document?.evidence_layers?.normalized?.series_count)
     const derivedSeriesCount = nonNegativeIntegerOrNull(document?.evidence_layers?.derived?.series_count)
     const mappedQualityFlags = AEROPONIC_HISTORY_QUALITY_FLAGS.filter((flag) => series.some((entry) => entry.qualityFlags.includes(flag)))
     if (normalizedSeriesCount === null || derivedSeriesCount === null
          || normalizedSeriesCount !== series.filter((entry) => entry.layer === "normalized").length
          || derivedSeriesCount !== series.filter((entry) => entry.layer === "derived").length
          || !equalMembers(documentQualityFlags, mappedQualityFlags)) return null

     return {
          backend: "timescaledb",
          range,
          bucketSeconds,
          aggregationMethod: safeText(document.quality.aggregation_method),
          dataFrom,
          to,
          cycleCount,
          sampleCount,
          eventCount,
          seriesCount: series.length,
          qualityFlags: documentQualityFlags,
          truncated: normalizedTruncated || derivedTruncated || series.some((entry) => entry.truncated),
          payloadIncluded: false,
          interpolates: false,
          series,
     }
}
